import { reconnects } from "./disconnect.ts";
import type { HudFeed } from "./hud/store.ts";
import { disconnectFailure } from "./loading-failure.ts";
import type { LoadingFailure } from "./loading-state.ts";
import { RESTORED_MARK_MS, Reconnection, afterDelay, pageReconnectDeps, type ReconnectDeps, type ReconnectWait } from "./reconnect.ts";
import type { FreshToken, SessionState } from "./session-types.ts";
import { say } from "../words.ts";

/**
 * What the background reconnect reads and calls on the session it serves.
 */
export interface ReconnectHost {
  readonly state: SessionState;
  readonly hud: Pick<HudFeed, "setConnection" | "setMessage" | "expire">;
  readonly account: boolean;
  readonly timing: Partial<Pick<ReconnectDeps, "schedule" | "random" | "now">> | undefined;
  /**
   * Makes sure the sign-in has not lapsed before a connection presents it; see `SessionOptions.freshToken`.
   */
  readonly freshToken: () => "ready" | Promise<FreshToken>;
  /**
   * Gives the connection up.
   */
  readonly drop: () => void;
  /**
   * Opens the connection the session joins on, with the token as it is.
   */
  readonly open: () => void;
  /**
   * Starts the loading step of one attempt.
   */
  readonly started: () => void;
  /**
   * Acts on what a refresh came to: joins, or ends the sign-in.
   */
  readonly openWith: (fresh: FreshToken) => void;
  /**
   * Takes the page out of the world with a notice.
   */
  readonly leave: (failure: LoadingFailure) => void;
}

/**
 * The background reconnect of a session (P1.9-T32, `reconnect.ts`): after a close a retry can cure the world is held
 * under the connection mark while the session joins again a few times, and the disconnect notice shows only when
 * those attempts are spent or an attempt ends in something a retry cannot cure.
 */
export class SessionReconnect {
  private readonly run: Reconnection;
  private lost: { readonly code: number; readonly reason: string } | null = null;
  private cancelRestoredMark: (() => void) | null = null;

  /**
   * @param host What the run reads and calls on its session.
   */
  public constructor(private readonly host: ReconnectHost) {
    this.run = new Reconnection(
      pageReconnectDeps(
        {
          open: () => this.tryAgain(),
          waiting: (wait) => this.showWaiting(wait),
          exhausted: () => this.exhausted(),
        },
        host.timing,
      ),
    );
  }

  /**
   * Whether the world is held while the session tries again: from the close until the new connection's first
   * snapshot. The scene sends nothing and moves nothing meanwhile.
   *
   * @returns True while held.
   */
  public get holding(): boolean {
    return this.host.state.reconnecting && !this.host.state.snapshotSeen;
  }

  /**
   * Takes a close after the reveal. A close a retry can cure holds the world and starts a run, or, inside a run, fails
   * the attempt.
   *
   * @param code The close code.
   * @param reason The close reason.
   * @returns True when the run took the close; false when the close is the notice's.
   */
  public close(code: number, reason: string): boolean {
    if (!reconnects(code) || this.host.state.signInEnded) {
      return false;
    }
    this.lost = { code, reason };
    this.hold();
    if (this.host.state.reconnecting) {
      this.run.failed();
    } else {
      this.begin();
    }
    return true;
  }

  /**
   * Takes a connection that was made in the background and said nothing in time: the attempt failed.
   *
   * @returns True when a run took it; false with no run.
   */
  public silent(): boolean {
    if (!this.host.state.reconnecting) {
      return false;
    }
    this.hold();
    this.run.failed();
    return true;
  }

  /**
   * Takes a connection given up with a failure (a refusal, a welcome naming another world or rules) inside a run: a
   * retry meets the same wall, so the notice shows at once.
   *
   * @param failure The failure.
   * @returns True when a run took it; false with no run.
   */
  public failure(failure: LoadingFailure): boolean {
    if (!this.host.state.reconnecting) {
      return false;
    }
    this.host.leave({ ...failure, category: "disconnected" });
    return true;
  }

  /**
   * Called after each drawn frame: once the connection tried again has welcomed the session and sent its first
   * snapshot, the world is the player's again, the line clears and the mark says the connection is back for
   * `RESTORED_MARK_MS`.
   */
  public mend(): void {
    const state = this.host.state;
    if (!state.reconnecting || !state.snapshotSeen) {
      return;
    }
    state.reconnecting = false;
    this.run.restored();
    this.lost = null;
    this.host.hud.expire(say("hud.connection.line"));
    this.host.hud.setConnection({ state: "restored" });
    const schedule = this.host.timing?.schedule ?? afterDelay;
    this.cancelRestoredMark = schedule(RESTORED_MARK_MS, () => {
      this.cancelRestoredMark = null;
      this.host.hud.setConnection(null);
    });
  }

  /**
   * Ends a run and its mark and line, as the page leaves the world.
   */
  public stop(): void {
    this.run.cancel();
    this.host.state.reconnecting = false;
    this.lost = null;
    this.cancelRestoredMark?.();
    this.cancelRestoredMark = null;
    this.host.hud.setConnection(null);
    this.host.hud.expire(say("hud.connection.line"));
  }

  /**
   * Gives the connection up, so the scene draws and sends nothing more, and the session reads as having no welcome and
   * no snapshot until the next connection's. What the world drew stays until that welcome replaces it.
   */
  private hold(): void {
    this.host.drop();
    this.host.state.welcomed = false;
    this.host.state.snapshotSeen = false;
  }

  /**
   * Begins a run: the mark and the line say so and the attempts run.
   */
  private begin(): void {
    this.host.state.reconnecting = true;
    this.cancelRestoredMark?.();
    this.cancelRestoredMark = null;
    this.host.hud.setMessage(say("hud.connection.line"));
    this.run.start();
  }

  /**
   * Tells the mark which attempt waits.
   *
   * @param wait The attempt and its delay.
   */
  private showWaiting(wait: ReconnectWait): void {
    this.host.hud.setConnection({ state: "reconnecting", attempt: wait.attempt, total: wait.total });
  }

  /**
   * Opens one attempt: the sign-in is refreshed first when it lapsed in the outage, an attempt that gets no answer
   * counting as failed, then the join is made as any join is, over the world already loaded.
   */
  private tryAgain(): void {
    const ready = this.host.freshToken();
    if (ready === "ready") {
      this.host.started();
      this.host.open();
      return;
    }
    void ready.then((fresh) => {
      if (!this.run.running) {
        return;
      }
      if (fresh === "failed") {
        this.run.failed();
        return;
      }
      if (fresh === "ready") {
        this.host.started();
      }
      this.host.openWith(fresh);
    });
  }

  /**
   * The attempts are spent: the world gives way to the disconnect notice for the close that began them.
   */
  private exhausted(): void {
    const lost = this.lost ?? { code: 1006, reason: "" };
    this.host.leave(disconnectFailure(lost.code, lost.reason, this.host.account));
  }
}
